import { AxiosResponse } from "axios";
import { flatten, orderBy } from "lodash";
import axios, { APIWrapperResponse, DefaultResponse } from "../general/HttpClient";
import { MatchViewModel } from "../matches/MatchesStore";
import { TeamViewModel } from "../teams/TeamsStore";
import { AliasViewModel } from "../aliases/AliasStore";
import { OrganizationInviteViewModel } from "../registration/RegistrationStore";

export function getTournaments(skip, limit, archived) {
    return axios.get("/api/Tournaments/getTournaments", { params: { skip, limit, archived } }).then((response: AxiosResponse<APIWrapperResponse<Array<TournamentViewModel>>>) => {
        return response.data.result;
    });
}

export function getTeamTournaments(id) {
    return axios.get(`/api/Tournaments/getTeamTournaments/${id}`).then((response: AxiosResponse<APIWrapperResponse<Array<TournamentViewModel>>>) => {
        return response.data.result;
    });
}

export function getAliasTournaments(id) {
    return axios.get(`/api/Tournaments/getAliasTournaments/${id}`).then((response: AxiosResponse<APIWrapperResponse<Array<TournamentViewModel>>>) => {
        return response.data.result;
    });
}

export function getTournament(id) {
    return axios.get(`/api/Tournaments/getTournament/${id}`).then((response: AxiosResponse<APIWrapperResponse<TournamentViewModel>>) => {
        return response.data.result;
    });
}

export function createTournament(request: CreateTournamentRequest) {
    return axios.post(`/api/Tournaments/createTournament`, request).then((response: AxiosResponse<APIWrapperResponse<CreateTournamentResponse>>) => {
        return response.data.result;
    });
}

export function updateTournament(request: TournamentViewModel | UpdateTournamentRequest) {
    return axios.put(`/api/Tournaments/updateTournament`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function addParticipant(request: AddTournamentParticipantRequest) {
    return axios.post(`/api/Tournaments/addParticipant`, request).then((response: AxiosResponse<APIWrapperResponse<AddTournamentParticipantResponse>>) => {
        return response.data.result;
    });
}

export function removeParticipant(request: RemoveTournamentParticipantRequest) {
    return axios.post(`/api/Tournaments/removeParticipant`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function updateParticipant(request: UpdateTournamentParticipantRequest) {
    return axios.put(`/api/Tournaments/updateParticipant`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function implementBracket(request: ImplementTournamentBracketRequest) {
    return axios.post(`/api/Tournaments/implementBracket`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function switchSides(request: SwitchTournamentMatchSidesRequest) {
    return axios.post(`/api/Tournaments/switchSides`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function copyTournament(request: CopyTournamentRequest) {
    return axios.post(`/api/Tournaments/copyTournament`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function resetTournament(request: ResetTournamentRequest) {
    return axios.post(`/api/Tournaments/resetTournament`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export class SwitchTournamentMatchSidesRequest {
    id: string;
    tournamentMatchID: number;
}

export class TournamentViewModel {
    constructor() {
        this.matches = [] as MatchViewModel[];
        this.participants = [];
        this.bracket = new TournamentBracketViewModel();
        this.organizationInvites = [];
    }
    id?: string;
    name: string;
    rules: string;
    rulesAsMarkdown: boolean;
    logoUri: string;
    splashUri: string;
    color: string;
    locked?: boolean;
    isPublic?: boolean;
    archived?: boolean;

    matches?: MatchViewModel[];
    participants?: TournamentParticipantViewModel[];

    bracketType: string;
    bracket?: TournamentBracketViewModel;

    organizationInvites: OrganizationInviteViewModel[];
}

export const UpcomingMatches = (tournament: TournamentViewModel) => {
    let matches = flatten(tournament.bracket?.rounds.map((round) => round.matches));

    let tournamentMatches = matches.filter((match) => match.match.participants.length >= 1);

    let upcomingMatches = orderBy(
        tournamentMatches.filter((match) => !match.winner),
        ["tournamentMatchID"],
        ["asc"]
    );
    return upcomingMatches || [];
};

export const PastMatches = (tournament: TournamentViewModel) => {
    let matches = flatten(tournament.bracket?.rounds.map((round) => round.matches));

    let tournamentMatches = matches.filter((match) => match.match.participants.length >= 1);

    let past = orderBy(
        tournamentMatches.filter((match) => match.winner),
        ["tournamentMatchID"],
        ["desc"]
    );
    return past || [];
};

export class CopyTournamentRequest {
    tournamentID: string;
}

export class CreateTournamentRequest {
    organizationID: string;
    name: string;
    rules: string;
    rulesAsMarkdown: boolean;
    logoUri: string;
    logoContent: string;
    splashUri: string;
    splashContent: string;
    color: string;
    isPublic: boolean;
    bracketType: string;
}

export interface CreateTournamentResponse extends DefaultResponse {
    tournamentID: string;
}

export interface UpdateTournamentRequest {
    id: string;
    name: string;
    rules: string;
    rulesAsMarkdown: boolean;
    logoUri: string;
    logoContent: string;
    splashUri: string;
    splashContent: string;
    color: string;
    isPublic: boolean;
    bracketType: string;
}

export interface TournamentParticipantViewModel {
    id: string;
    teamID: string;
    team: TeamViewModel;
    seed: number;
    aliases: AliasTournamentParticipantViewModel[];
}

export interface AliasTournamentParticipantViewModel {
    id: string;
    participantID: string;
    aliasID: string;
    alias: AliasViewModel;
}

export interface RemoveTournamentParticipantRequest {
    tournamentID: string;
    teamID: string;
}

export interface AddTournamentParticipantResponse extends DefaultResponse {
    participantID: string;
}

export interface AddTournamentParticipantRequest {
    tournamentID: string;
    teamID: string;
    seed?: number;
    aliases: string[];
}

export class TournamentMatchViewModel {
    roundID: number;
    tournamentMatchID: number;
    nextTournamentMatchID: number;
    loserTournamentMatchID: number;
    participantOne: string;
    participantOneSourceTournamentMatchID: number;
    participantOneLoser: boolean;
    participantTwo: string;
    participantTwoSourceTournamentMatchID: number;
    participantTwoLoser: boolean;
    winner: string;
    match: MatchViewModel;
    loserMatch: boolean;
    loserBracket: boolean;
}
export interface TournamentRoundViewModel {
    roundID: number;
    matchCount: number;
    matches: TournamentMatchViewModel[];
}

export class TournamentBracketViewModel {
    constructor() {
        this.rounds = [];
        this.finals = new TournamentMatchViewModel();
    }
    rounds: TournamentRoundViewModel[];
    matchCount: number;
    maxSlots: number;
    finals: TournamentMatchViewModel;
}

export interface ImplementTournamentBracketRequest {
    tournamentID: string;
}

export class UpdateTournamentParticipantRequest {
    constructor(participantID: string, seed: number, aliasIDs: string[]) {
        this.participantID = participantID;
        this.seed = seed;
        this.aliasIDs = aliasIDs;
    }
    aliasIDs: string[];
    participantID: string;
    seed: number;
}

export interface ResetTournamentRequest {
    tournamentID: string;
}
